
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import SubDivisionService from "../../services/subdivision.service";

// Common error handler
const getErrorMessage = (error) => {
  return (
    error.response?.data?.message ||
    error.message ||
    "Something went wrong"
  );
};

// GET ALL SUBDIVISIONS
export const getSubDivisions = createAsyncThunk(
  "subdivision/getSubDivisions",
  async (params = {}, thunkAPI) => {
    try {
      const response = await SubDivisionService.getSubDivisions(params);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch subdivisions"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// GET SUBDIVISION BY ID
export const getSubDivisionById = createAsyncThunk(
  "subdivision/getSubDivisionById",
  async (id, thunkAPI) => {
    try {
      const response = await SubDivisionService.getSubDivisionById(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch subdivision details"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// CREATE SUBDIVISION
export const createSubDivision = createAsyncThunk(
  "subdivision/createSubDivision",
  async (payload, thunkAPI) => {
    try {
      const response = await SubDivisionService.createSubDivision(payload);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to create subdivision"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// UPDATE SUBDIVISION
export const updateSubDivision = createAsyncThunk(
  "subdivision/updateSubDivision",
  async ({ id, payload }, thunkAPI) => {
    try {
      const response = await SubDivisionService.updateSubDivision(
        id,
        payload
      );

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to update subdivision"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// DELETE SUBDIVISION
export const deleteSubDivision = createAsyncThunk(
  "subdivision/deleteSubDivision",
  async (id, thunkAPI) => {
    try {
      const response = await SubDivisionService.deleteSubDivision(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to delete subdivision"
        );
      }

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState = {
  subdivisions: [],
  selectedSubDivision: null,

  loading: false,
  detailsLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  error: null,
  detailsError: null,
  successMessage: null,

  pagination: {
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
  },
};

const subDivisionSlice = createSlice({
  name: "subdivision",
  initialState,

  reducers: {
    clearSubDivisionError: (state) => {
      state.error = null;
      state.detailsError = null;
    },

    clearSubDivisionMessage: (state) => {
      state.successMessage = null;
    },

    clearSelectedSubDivision: (state) => {
      state.selectedSubDivision = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // GET ALL SUBDIVISIONS
      .addCase(getSubDivisions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getSubDivisions.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload || {};

        // API response: data.subDivisions
        state.subdivisions = data.subDivisions || [];

        state.pagination = {
          total: data.total ?? 0,
          currentPage: data.currentPage ?? 1,
          totalPages: data.totalPages ?? 0,
          limit: data.limit ?? 10,
        };
      })

      .addCase(getSubDivisions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // GET SUBDIVISION BY ID
      .addCase(getSubDivisionById.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })

      .addCase(getSubDivisionById.fulfilled, (state, action) => {
        state.detailsLoading = false;

        const data = action.payload || {};
        state.selectedSubDivision = data.subDivision || data.subdivision || data;
      })

      .addCase(getSubDivisionById.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload || action.error.message;
      })

      // CREATE SUBDIVISION
      .addCase(createSubDivision.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(createSubDivision.fulfilled, (state, action) => {
        state.createLoading = false;

        state.successMessage =
          action.payload?.message || "SubDivision created successfully";

        const data = action.payload?.data;
        const subdivision =
          data?.subDivision || data?.subdivision || data;

        if (subdivision?._id) {
          state.subdivisions.unshift(subdivision);
          state.pagination.total += 1;
        }
      })

      .addCase(createSubDivision.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload || action.error.message;
      })

      // UPDATE SUBDIVISION
      .addCase(updateSubDivision.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(updateSubDivision.fulfilled, (state, action) => {
        state.updateLoading = false;

        state.successMessage =
          action.payload?.message || "SubDivision updated successfully";

        const data = action.payload?.data;
        const updatedSubDivision =
          data?.subDivision || data?.subdivision || data;

        if (updatedSubDivision?._id) {
          const index = state.subdivisions.findIndex(
            (subdivision) =>
              subdivision._id === updatedSubDivision._id
          );

          if (index !== -1) {
            state.subdivisions[index] = updatedSubDivision;
          }

          if (
            state.selectedSubDivision?._id === updatedSubDivision._id
          ) {
            state.selectedSubDivision = updatedSubDivision;
          }
        }
      })

      .addCase(updateSubDivision.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || action.error.message;
      })

      // DELETE SUBDIVISION
      .addCase(deleteSubDivision.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(deleteSubDivision.fulfilled, (state, action) => {
        state.deleteLoading = false;

        state.subdivisions = state.subdivisions.filter(
          (subdivision) => subdivision._id !== action.payload
        );

        if (state.selectedSubDivision?._id === action.payload) {
          state.selectedSubDivision = null;
        }

        state.pagination.total = Math.max(
          0,
          state.pagination.total - 1
        );

        state.successMessage = "SubDivision deleted successfully";
      })

      .addCase(deleteSubDivision.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const {
  clearSubDivisionError,
  clearSubDivisionMessage,
  clearSelectedSubDivision,
} = subDivisionSlice.actions;

export default subDivisionSlice.reducer;
